/* eslint-disable @typescript-eslint/no-explicit-any */
import * as _ from 'lodash-es';
import { GridComponent } from '@progress/kendo-angular-grid';
import { DataResult, State } from '@progress/kendo-data-query';
import { PathSegment } from './../Group-Result/path-segment';
import { GroupResultMerger } from './../Group-Result/group-result-merger';
import { GroupCollapseExpandUtils } from './../Grouping/Utils/group-collapse-expand-utils';
import { GroupOData } from './OData/group-odata';
import { GroupingExSignalViewModel } from './grouping-ex-signal.view-model';
import { GroupingExLoadResult } from './grouping-ex.load';

/**
 * Signal-based version of GroupingExLoad
 * Uses async/await instead of Observables for cleaner code and better error handling
 */
export class GroupingExSignalLoad {
  /**
   * Load data asynchronously and return result
   */
  public static async loadData(
    vm: GroupingExSignalViewModel,
    grid: GridComponent,
    state: State,
    signal?: AbortSignal
  ): Promise<GroupingExLoadResult> {
    const stateCopy = _.cloneDeep(state) as State;

    if (stateCopy.group && stateCopy.group.length) {
      // Load group(s)
      const odataQuery = GroupOData.getGroupQuery(
        stateCopy,
        null,
        vm.linkedAggregateFields,
        true,
        vm.loadGroupsByLevels() ? 1 : 0,
        'count',
        vm.customODataAggregateExpression
      );

      const d = await vm.loadData(odataQuery, signal);
      if (!d) return new GroupingExLoadResult();

      const serverData = d['value'];
      const serverTotalCount = parseInt(d['@odata.count'], 10);

      return new GroupingExLoadResult().addGroupDataSet(
        { data: serverData, total: serverTotalCount } as DataResult,
        GroupResultMerger.processGroups(
          null,
          serverData,
          state,
          vm.linkedAggregateFields,
          vm.loadGroupsByLevels() ? 1 : 0
        )
      );
    } else {
      // Load items - in case there's no grouping we need to do skip/take!
      const odataQuery = GroupOData.getItemQuery(
        stateCopy,
        null,
        vm.loadAllItemsToGroups(),
        vm.oDataSettings
      );

      const d = await vm.loadData(odataQuery, signal);
      if (!d) return new GroupingExLoadResult();

      const serverData = d['value'];
      const serverTotalCount = parseInt(d['@odata.count'], 10);

      return new GroupingExLoadResult().addItemDataSet(
        { data: serverData, total: serverTotalCount } as DataResult,
        GroupResultMerger.processItems(serverData, state)
      );
    }
  }

  /**
   * Merge loaded data result into grid
   */
  public static mergeLoadDataResultToGrid(
    vm: GroupingExSignalViewModel,
    grid: GridComponent,
    state: State,
    result: GroupingExLoadResult,
    newItem: any = null
  ): void {
    if (result.processedGroupSets && result.processedGroupSets.length) {
      const processedData = result.processedGroupSets[0];
      const total = result.loadedGroupSets[0].total;

      // Create a new GridDataResult with the correct total
      grid.data = {
        data: processedData.data,
        total: total,
      };

      GroupingExSignalLoad.addNewItem(newItem, grid.data.data);

      // expanding - if all groups were loaded, expand all (keep all expanded and collapse just items), otherwise collapse all beneath...
      if (vm.loadGroupsByLevels()) {
        GroupCollapseExpandUtils.collapseAll(grid, grid.data.data);
      } else {
        GroupCollapseExpandUtils.expandAllCollapseLast(
          grid,
          state,
          grid.data.data
        );
      }
    } else if (result.processedItemSets && result.processedItemSets.length) {
      const processedData = result.processedItemSets[0];
      const total = result.loadedItemSets[0].total;

      // Create a new GridDataResult with the correct total
      grid.data = {
        data: processedData.data,
        total: total,
      };

      GroupingExSignalLoad.addNewItem(newItem, grid.data.data);
    }
  }

  /**
   * Add new item to data if not already present
   */
  public static addNewItem(newItem: any, data: any[]): void {
    if (newItem != null) {
      const newItemIndex = data.findIndex(
        (x) => JSON.stringify(x) === JSON.stringify(newItem)
      );
      if (newItemIndex === -1) {
        data.unshift(newItem);
      } else {
        const tmpData = data[newItemIndex];
        data[newItemIndex] = data[0];
        data[0] = tmpData;
      }
    }
  }

  /**
   * Load next level of data (either groups or items)
   */
  public static async loadNextLevel(
    vm: GroupingExSignalViewModel,
    grid: GridComponent,
    state: State,
    groupDataItem: any,
    signal?: AbortSignal
  ): Promise<GroupingExLoadResult> {
    const stateCopy = _.cloneDeep(state) as State;
    const gridData = grid.data as any;
    const groupDataItemPath =
      PathSegment.getPathToDataItem(gridData.data, groupDataItem) || [];

    const loadGroups =
      !stateCopy.group || groupDataItemPath.length < stateCopy.group.length;

    let odataQuery: string;

    if (loadGroups) {
      odataQuery = GroupOData.getGroupQuery(
        stateCopy,
        groupDataItemPath,
        vm.linkedAggregateFields,
        false,
        vm.loadGroupsByLevels() ? 1 : 0,
        'count',
        vm.customODataAggregateExpression
      );
    } else {
      odataQuery = GroupOData.getItemQuery(
        stateCopy,
        groupDataItemPath,
        vm.loadAllItemsToGroups(),
        vm.oDataSettings
      );
    }

    const d = await vm.loadData(odataQuery, signal);
    if (!d) return new GroupingExLoadResult();

    const serverData = d['value'];
    const serverTotalCount = parseInt(d['@odata.count'], 10);

    const loadResult = new GroupingExLoadResult();
    if (loadGroups) {
      loadResult.addGroupDataSet(
        { data: serverData, total: serverTotalCount } as DataResult,
        GroupResultMerger.processGroups(
          groupDataItemPath,
          serverData,
          state,
          vm.linkedAggregateFields,
          vm.loadGroupsByLevels() ? 1 : 0
        )
      );
    } else {
      loadResult.addItemDataSet(
        { data: serverData, total: serverTotalCount } as DataResult,
        GroupResultMerger.processItems(serverData, state)
      );
    }

    return loadResult;
  }

  /**
   * Merge next level load result into grid
   */
  public static mergeLoadNextLevelResultToGrid(
    vm: GroupingExSignalViewModel,
    grid: GridComponent,
    state: State,
    groupDataItem: any,
    result: GroupingExLoadResult
  ): void {
    const gridData = grid.data as any;

    if (result.processedGroupSets && result.processedGroupSets.length) {
      GroupResultMerger.mergeSubTreeOfProcessedNewItemsWithGrid(
        gridData.data,
        result.processedGroupSets[0].data
      );
      // CRITICAL: Kendo 21.x requires new array reference for items
      if (groupDataItem) {
        groupDataItem.items = [...(groupDataItem.items || [])];
      }
      GroupCollapseExpandUtils.collapseSubtree(grid, groupDataItem);
    } else if (result.processedItemSets && result.processedItemSets.length) {
      groupDataItem['items'] = [];
      GroupResultMerger.mergeSubTreeOfProcessedNewItemsWithGrid(
        gridData.data,
        result.processedItemSets[0].data
      );
    }

    // CRITICAL: Kendo 21.x requires a new reference to grid.data for change detection
    // After merging data in place, we need to reassign with a new object reference
    // We must also create a new array reference for the data property
    grid.data = {
      data: [...gridData.data],
      total: gridData.total,
    };
  }

  /**
   * Load levels below current level
   */
  public static async loadLevelsBelow(
    vm: GroupingExSignalViewModel,
    grid: GridComponent,
    state: State,
    groupDataItem: any,
    signal?: AbortSignal
  ): Promise<GroupingExLoadResult> {
    const stateCopy = _.cloneDeep(state) as State;
    const gridData = grid.data as any;

    const groupDataItemPath =
      PathSegment.getPathToDataItem(gridData.data, groupDataItem) || [];

    // Load all items (in 1 call)
    const itemOdataQuery = GroupOData.getItemQuery(
      stateCopy,
      groupDataItemPath,
      vm.loadAllItemsToGroups(),
      vm.oDataSettings
    );
    const itemPromise = vm.loadData(itemOdataQuery, signal);

    // Load group levels
    const groupPromises: Promise<any>[] = [];
    const levels = stateCopy.group.length - groupDataItemPath.length;

    for (let i = 0; i < levels; i++) {
      const groupOdataQuery = GroupOData.getGroupQuery(
        stateCopy,
        groupDataItemPath,
        vm.linkedAggregateFields,
        false,
        i + 1,
        'count',
        vm.customODataAggregateExpression
      );
      groupPromises.push(vm.loadData(groupOdataQuery, signal));
    }

    const [groupResults, itemResult] = await Promise.all([
      Promise.all(groupPromises),
      itemPromise,
    ]);

    const loadResult = new GroupingExLoadResult();

    // Process groups
    if (groupResults) {
      groupResults.forEach((gdOdata, gdIndex) => {
        if (!gdOdata) return;
        const groupData = gdOdata['value'];
        const groupTotalCount = parseInt(gdOdata['@odata.count'], 10);

        loadResult.addGroupDataSet(
          { data: groupData, total: groupTotalCount } as DataResult,
          GroupResultMerger.processGroups(
            groupDataItemPath,
            groupData,
            state,
            vm.linkedAggregateFields,
            gdIndex + 1
          )
        );
      });
    }

    // Process items
    if (itemResult) {
      const itemData = itemResult['value'];
      const itemTotalCount = parseInt(itemResult['@odata.count'], 10);

      loadResult.addItemDataSet(
        { data: itemData, total: itemTotalCount } as DataResult,
        GroupResultMerger.processItems(itemData, state)
      );
    }

    return loadResult;
  }

  /**
   * Merge sub-level data into grid
   */
  public static mergeLoadLevelsBelowResultToGrid(
    vm: GroupingExSignalViewModel,
    grid: GridComponent,
    state: State,
    groupDataItem: any,
    result: GroupingExLoadResult
  ): void {
    const gridData = grid.data as any;

    result.processedGroupSets.forEach((gds) => {
      GroupResultMerger.mergeSubTreeOfProcessedNewItemsWithGrid(
        gridData.data,
        gds.data
      );
    });

    result.processedItemSets.forEach((ids) => {
      const mergeItemsToExistingAggregatesOnly = vm.loadAllItemsToGroups();
      GroupResultMerger.mergeSubTreeOfProcessedNewItemsWithGrid(
        gridData.data,
        ids.data,
        mergeItemsToExistingAggregatesOnly
      );
    });

    if (vm.autoExpandGroupOnSelection()) {
      GroupCollapseExpandUtils.expandSubtree(grid, groupDataItem, true);
    } else {
      GroupCollapseExpandUtils.collapseSubtree(grid, groupDataItem);
    }

    // CRITICAL: Kendo 21.x requires a new reference to grid.data for change detection
    // After merging data in place, we need to reassign with a new object reference
    // We must also create a new array reference for the data property
    grid.data = {
      data: [...gridData.data],
      total: gridData.total,
    };
  }
}
